import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button, Checkbox, DatePicker, Label, Modal } from '@shared/components';
import { dateKeyToLocalDate, toDateKey, type DateKey } from '@features/quote/utils/dateKey';

export interface ScheduleDateEditModalProps {
    isOpen: boolean;
    serviceName: string;
    currentDate: DateKey;
    // max(today, previous row's date) — D8 and D9 enforced at the affordance.
    minDate: DateKey;
    // The following row's date; applied ONLY while the cascade box is unticked.
    uncascadedMaxDate?: DateKey;
    isSubmitting: boolean;
    error?: string | null;
    onSubmit: (date: DateKey, cascade: boolean) => void;
    onClose: () => void;
}

const CASCADE_BOUND_ERROR =
    'This date is later than the following service. Tick "Also update the following services" to move it, or choose an earlier date.';

// Props in, callbacks out. Owns only its own draft state; everything
// else is orchestrated by useScheduleOverrides via DerivedServicesPanel.
export const ScheduleDateEditModal: React.FC<ScheduleDateEditModalProps> = ({
    isOpen,
    serviceName,
    currentDate,
    minDate,
    uncascadedMaxDate,
    isSubmitting,
    error,
    onSubmit,
    onClose,
}) => {
    const headingId = useId();
    const [selectedDate, setSelectedDate] = useState<DateKey>(currentDate);
    const [cascade, setCascade] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    // A fresh draft every time the modal opens (D12: cascade unticked by
    // default), so a previous row's edit never bleeds into the next.
    useEffect(() => {
        if (!isOpen) return;
        setSelectedDate(currentDate);
        setCascade(false);
        setLocalError(null);
    }, [isOpen, currentDate]);

    const handleClose = useCallback(() => {
        if (isSubmitting) return;
        onClose();
    }, [isSubmitting, onClose]);

    const handleDateChange = useCallback((date: Date | null) => {
        const key = toDateKey(date);
        if (!key) return;
        setSelectedDate(key);
        setLocalError(null);
    }, []);

    const handleCascadeChange = useCallback((checked: boolean) => {
        setCascade(checked);
        setLocalError(null);
    }, []);

    const handleSubmit = useCallback(() => {
        if (!cascade && uncascadedMaxDate && selectedDate > uncascadedMaxDate) {
            setLocalError(CASCADE_BOUND_ERROR);
            return;
        }
        setLocalError(null);
        onSubmit(selectedDate, cascade);
    }, [cascade, uncascadedMaxDate, selectedDate, onSubmit]);

    // Initial focus deliberately skips the date input: flatpickr opens on focus,
    // which would pop the calendar open before the dealer asks for it.
    const cascadeFieldRef = useRef<HTMLDivElement>(null);
    const displayedError = localError ?? error ?? null;
    const errorId = `${headingId}-error`;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            showCloseButton={!isSubmitting}
            ariaLabelledBy={headingId}
            initialFocusRef={cascadeFieldRef}
            className="max-w-md p-6"
        >
            <h2 id={headingId} className="text-lg font-semibold mb-4">
                Edit date for {serviceName}
            </h2>
            <div className="mb-4">
                <Label htmlFor="schedule-date-edit-picker">Service date</Label>
                <DatePicker
                    id="schedule-date-edit-picker"
                    onChange={handleDateChange}
                    defaultDate={dateKeyToLocalDate(selectedDate)}
                    minDate={dateKeyToLocalDate(minDate)}
                    maxDate={cascade ? undefined : dateKeyToLocalDate(uncascadedMaxDate)}
                    disabled={isSubmitting}
                    error={!!displayedError}
                    aria-invalid={!!displayedError}
                    aria-describedby={displayedError ? errorId : undefined}
                />
            </div>
            <div className="mb-4" ref={cascadeFieldRef}>
                <Checkbox
                    id="schedule-date-edit-cascade"
                    label="Also update the following services"
                    ariaLabel="Also update the following services"
                    checked={cascade}
                    onChange={handleCascadeChange}
                    disabled={isSubmitting}
                />
            </div>
            {displayedError && (
                <p id={errorId} role="alert" className="mb-4 text-sm text-mp-error">
                    {displayedError}
                </p>
            )}
            <div className="flex justify-end gap-2">
                <Button type="button" variant="clear" disabled={isSubmitting} onClick={handleClose}>
                    Cancel
                </Button>
                <Button type="button" variant="primary" disabled={isSubmitting} onClick={handleSubmit}>
                    {isSubmitting ? 'Saving…' : 'Save'}
                </Button>
            </div>
        </Modal>
    );
};
